import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import BrandDashboardLayout from "../../components/BrandDashboardLayout";
import ResponsivenessBadge from "../../components/ResponsivenessBadge";
import MatcherWizard from "../../components/MatcherWizard";
import { toast } from "sonner";
import { Sparkles, Lock, ArrowRight, BookmarkCheck, Send, RefreshCw, Brain, TrendingUp, MessageCircle, SlidersHorizontal } from "lucide-react";
import { isBrandPro } from "../../lib/plan";

const SPORTS = ["Football","Basketball","Athletics","MMA / Combat","Cycling","Rugby","Cricket","Tennis","Golf","Esports","Swimming","Padel"];
const REGIONS = ["UK","London","Manchester","Europe","Middle East","USA","Asia","Global"];
const DELIVERABLES = ["Reels","Stories","TikTok","YouTube","Appearances","Long-form video","Photo content","Newsletter feature"];
const GOALS = ["Awareness","Conversion","UGC","Community","Product launch","Retention"];

export default function Matcher() {
  const { user } = useAuth();
  const [mode, setMode] = useState("wizard"); // "wizard" | "structured"
  const [form, setForm] = useState({
    brief: "", budget_range: "", sports: [], regions: [], audience_size_min: "",
    deliverables: [], timeline: "", campaign_goal: "", top_n: 10,
  });
  const [results, setResults] = useState(null);
  const [running, setRunning] = useState(false);

  const isPro = isBrandPro(user);
  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const toggle = (k, v) => upd(k, form[k].includes(v) ? form[k].filter(x => x !== v) : [...form[k], v]);

  const run = async (override) => {
    const source = override || form;
    if ((source.brief || "").trim().length < 20) return toast.error("Brief must be at least 20 characters — describe the campaign.");
    setRunning(true); setResults(null);
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 75000);
    try {
      const payload = { ...source };
      payload.audience_size_min = payload.audience_size_min ? parseInt(payload.audience_size_min, 10) : null;
      const { data } = await api.post("/brand/matcher", payload, { signal: controller.signal });
      setResults(data);
      toast.success(`Ranked ${data.matches?.length || 0} of ${data.candidate_count} candidates`);
    } catch (e) {
      if (e.name === "CanceledError" || e.code === "ERR_CANCELED") {
        toast.error("AI matcher took too long. Try a shorter, more specific brief.");
      } else {
        toast.error(e.response?.data?.detail || "Matcher failed");
      }
    } finally { clearTimeout(timer); setRunning(false); }
  };

  // Wizard finished → hydrate form (so user can see/tweak it) then run.
  const handleWizardRun = (payload) => {
    setForm({
      brief: payload.brief || "",
      budget_range: payload.budget_range || "",
      sports: payload.sports || [],
      regions: payload.regions || [],
      audience_size_min: payload.audience_size_min ?? "",
      deliverables: payload.deliverables || [],
      timeline: payload.timeline || "",
      campaign_goal: payload.campaign_goal || "",
      top_n: payload.top_n || 10,
    });
    run(payload);
  };

  if (!isPro) {
    return (
      <BrandDashboardLayout>
        <div className="p-6 md:p-12 max-w-3xl">
          <div className="label-eyebrow">Brand Pro</div>
          <h1 className="font-heading text-4xl md:text-5xl uppercase tracking-tighter mt-2" data-testid="matcher-title">AI Athlete Matcher</h1>
          <div className="surface p-8 mt-8 border-l-2 border-volt" data-testid="matcher-paywall">
            <Lock className="w-6 h-6 text-volt mb-4" />
            <div className="font-heading text-2xl uppercase">Locked behind Brand Pro</div>
            <p className="text-ink-muted text-sm mt-3 max-w-md">
              Describe your campaign in plain English and let our AI rank the network by fit score — with <span className="text-white">reasoning</span> for every athlete. It's the LinkedIn Recruiter wedge for sports sponsorships.
            </p>
            <Link to="/app/pricing" className="btn-volt inline-flex items-center gap-2 mt-6 text-sm" data-testid="matcher-upgrade-btn">
              See Brand Pro <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </BrandDashboardLayout>
    );
  }

  return (
    <BrandDashboardLayout>
      <div className="p-6 md:p-12">
        <div className="mb-10 max-w-3xl">
          <div className="label-eyebrow">Brand Pro · AI</div>
          <h1 className="font-heading text-4xl md:text-5xl uppercase tracking-tighter mt-2" data-testid="matcher-title">AI Athlete Matcher</h1>
          <p className="text-ink-muted mt-3 text-sm">Describe your campaign. We'll rank the network by fit and explain why each athlete is a strong match.</p>
        </div>

        {/* Mode toggle: Wizard (conversational) vs Structured (form) */}
        <div className="flex items-center gap-2 mb-6" data-testid="matcher-mode-toggle">
          <button onClick={() => setMode("wizard")}
            data-testid="matcher-mode-wizard"
            className={`inline-flex items-center gap-2 px-4 py-2 text-[11px] uppercase tracking-widest border ${
              mode === "wizard" ? "border-volt text-volt bg-volt/5" : "border-line text-ink-muted hover:text-white"
            }`}>
            <MessageCircle className="w-3.5 h-3.5" /> Chat with AI
          </button>
          <button onClick={() => setMode("structured")}
            data-testid="matcher-mode-structured"
            className={`inline-flex items-center gap-2 px-4 py-2 text-[11px] uppercase tracking-widest border ${
              mode === "structured" ? "border-volt text-volt bg-volt/5" : "border-line text-ink-muted hover:text-white"
            }`}>
            <SlidersHorizontal className="w-3.5 h-3.5" /> Structured form
          </button>
          {running && <span className="text-[10px] uppercase tracking-widest text-ink-muted ml-2"><RefreshCw className="w-3 h-3 animate-spin inline mr-1" /> Ranking…</span>}
        </div>

        {mode === "wizard" && (
          <div className="mb-10">
            <MatcherWizard onRunReady={handleWizardRun} />
          </div>
        )}

        {mode === "structured" && (
        <div className="grid md:grid-cols-2 gap-6 mb-10">
          {/* LEFT: Structured fields */}
          <div className="surface p-5 space-y-5" data-testid="matcher-structured">
            <div className="label-eyebrow">Filters & metadata</div>
            <Field label="Budget range">
              <input value={form.budget_range} onChange={e => upd("budget_range", e.target.value)} placeholder="£2K-£5K per athlete" data-testid="matcher-budget"
                className="w-full bg-bg border border-line focus:border-volt outline-none px-3 py-2.5 text-white text-sm" />
            </Field>
            <Field label="Sports">
              <Chips options={SPORTS} selected={form.sports} onToggle={v => toggle("sports", v)} testidPrefix="matcher-sport" />
            </Field>
            <Field label="Regions">
              <Chips options={REGIONS} selected={form.regions} onToggle={v => toggle("regions", v)} testidPrefix="matcher-region" />
            </Field>
            <Field label="Min audience size">
              <input type="number" value={form.audience_size_min} onChange={e => upd("audience_size_min", e.target.value)} placeholder="10000" data-testid="matcher-audience-min"
                className="w-full bg-bg border border-line focus:border-volt outline-none px-3 py-2.5 text-white text-sm" />
            </Field>
            <Field label="Deliverables">
              <Chips options={DELIVERABLES} selected={form.deliverables} onToggle={v => toggle("deliverables", v)} testidPrefix="matcher-deliverable" />
            </Field>
            <Field label="Timeline">
              <input value={form.timeline} onChange={e => upd("timeline", e.target.value)} placeholder="Apr-Jun 2026" data-testid="matcher-timeline"
                className="w-full bg-bg border border-line focus:border-volt outline-none px-3 py-2.5 text-white text-sm" />
            </Field>
            <Field label="Campaign goal">
              <select value={form.campaign_goal} onChange={e => upd("campaign_goal", e.target.value)} data-testid="matcher-goal"
                className="w-full bg-bg border border-line focus:border-volt outline-none px-3 py-2.5 text-white text-sm">
                <option value="">Select a goal…</option>
                {GOALS.map(g => <option key={g}>{g}</option>)}
              </select>
            </Field>
          </div>
          {/* RIGHT: Free-text brief */}
          <div className="surface p-5 flex flex-col" data-testid="matcher-brief-card">
            <div className="label-eyebrow mb-2">Campaign brief (free-text)</div>
            <textarea rows={14} value={form.brief} onChange={e => upd("brief", e.target.value)}
              placeholder="e.g. We're launching a recovery drink targeting hybrid athletes and runners aged 18-30. Looking for creators with strong engagement and authentic training content. We care more about authenticity than reach — small audiences with high retention beat huge audiences with vanity follows. Open to long-term partnerships if the chemistry's there." maxLength={2000}
              data-testid="matcher-brief"
              className="flex-1 w-full bg-bg border border-line focus:border-volt outline-none px-4 py-3 text-white text-sm resize-none" />
            <div className="text-[10px] uppercase tracking-widest text-ink-muted mt-2">{form.brief.length}/2000 · Be specific — better briefs → better matches</div>
            <button onClick={run} disabled={running || form.brief.trim().length < 20}
              className="btn-volt w-full mt-4 inline-flex items-center justify-center gap-2 disabled:opacity-40" data-testid="matcher-run-btn">
              {running ? <><RefreshCw className="w-4 h-4 animate-spin" /> Ranking with AI… (up to 60s)</> : <><Brain className="w-4 h-4" /> Find my athletes</>}
            </button>
          </div>
        </div>
        )}

        {/* RESULTS */}
        {results && (
          <div data-testid="matcher-results">
            <div className="surface p-5 mb-6 border-volt/30" data-testid="matcher-summary">
              <div className="flex items-start gap-3">
                <Sparkles className="w-5 h-5 text-volt shrink-0 mt-0.5" />
                <div>
                  <div className="label-eyebrow text-volt">AI summary</div>
                  <p className="text-sm mt-2 leading-relaxed">{results.summary || "No summary returned."}</p>
                  <p className="text-[10px] uppercase tracking-widest text-ink-muted mt-3">
                    Ranked from {results.candidate_count} pre-filtered candidates
                  </p>
                </div>
              </div>
            </div>
            {(results.matches || []).length === 0 ? (
              <div className="surface p-10 text-center text-ink-muted">No matches. Loosen the structured filters or rewrite the brief.</div>
            ) : (
              <div className="grid md:grid-cols-2 gap-4">
                {(results.matches || []).map(m => <MatchCard key={m.athlete_user_id} m={m} />)}
              </div>
            )}
          </div>
        )}
      </div>
    </BrandDashboardLayout>
  );
}

function MatchCard({ m }) {
  return (
    <div className="surface p-5" data-testid={`matcher-result-${m.athlete_user_id}`}>
      <div className="flex justify-between items-start gap-3">
        <div className="min-w-0">
          <Link to={`/app/brand/athlete/${m.athlete_user_id}`} className="font-heading text-xl uppercase tracking-tight hover:text-volt truncate block">{m.full_name}</Link>
          <div className="text-xs text-ink-muted mt-1">{m.sport}{m.level && ` · ${m.level}`}{m.location && ` · ${m.location}`}</div>
          {m.audience_size_label && <div className="text-xs text-ink-muted mt-1">{m.audience_size_label} audience</div>}
        </div>
        <div className="text-right shrink-0">
          <div className="text-volt font-mono text-4xl leading-none" data-testid={`matcher-fit-${m.athlete_user_id}`}>{m.fit_score}</div>
          <div className="text-[9px] uppercase tracking-widest text-ink-muted mt-1">Fit score</div>
        </div>
      </div>
      <p className="text-sm text-ink mt-4 leading-relaxed" data-testid={`matcher-reason-${m.athlete_user_id}`}>{m.reasoning}</p>
      {m.signals?.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-4">
          {m.signals.map((s, i) => (
            <span key={i} className="text-[9px] uppercase tracking-widest border border-volt/40 text-volt px-2 py-1">{s}</span>
          ))}
        </div>
      )}
      {m.responsiveness && m.responsiveness.band !== "new" && (
        <div className="mt-3">
          <ResponsivenessBadge r={m.responsiveness} size="sm" inline testid={`matcher-responsiveness-${m.athlete_user_id}`} />
        </div>
      )}
      <Link to={`/app/brand/athlete/${m.athlete_user_id}`} className="text-volt text-xs uppercase tracking-widest mt-5 inline-flex items-center gap-1">
        Open profile <ArrowRight className="w-3 h-3" />
      </Link>
    </div>
  );
}

function Field({ label, children }) { return <div><label className="label-eyebrow block mb-2 text-[10px]">{label}</label>{children}</div>; }
function Chips({ options, selected, onToggle, testidPrefix }) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map(o => (
        <button key={o} type="button" onClick={() => onToggle(o)} data-testid={`${testidPrefix}-${o}`}
          className={`px-2.5 py-1.5 border text-[10px] uppercase tracking-widest ${selected.includes(o) ? "border-volt text-volt" : "border-line text-ink-muted hover:text-white"}`}>{o}</button>
      ))}
    </div>
  );
}
